import { useState } from 'react';
import { Container, Box, Stack, TextField, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../redux/hooks';
import { createDocumentType, fetchDocumentType } from '../../../redux/modules/b2bThriftPlan/companies/company.actions';
import { documentTypeCreateLoadingSelector } from '../../../redux/modules/b2bThriftPlan/companies/company.selectors';
import { locations } from '../../../routes/locations';

export const CreateDocumentType = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isSubmitting = useSelector(documentTypeCreateLoadingSelector);

  const [type, setType] = useState('');
  const [typeError, setTypeError] = useState<string | null>(null);

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setType(event.target.value);

    if (typeError && event.target.value.trim().length > 0) {
      setTypeError(null);
    }
  };

  const handleCancel = () => {
    navigate(locations.documentTypes());
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedType = type.trim();

    if (!trimmedType) {
      setTypeError('Document type is required');
      return;
    }

    try {
      const response = await dispatch(createDocumentType({ type: trimmedType })).unwrap();
      const successMessage = response?.data?.message || `Document type "${trimmedType}" created successfully`;

      enqueueSnackbar(successMessage, { variant: 'success' });

      await dispatch(fetchDocumentType());

      navigate(locations.documentTypes());
    } catch (error: any) {
      const errorMessage =
        error?.payload?.message || error?.message || 'Failed to create document type. Please try again.';

      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">Create Document Type</Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>
          <TextField
            label="Type"
            value={type}
            onChange={handleTypeChange}
            required
            error={!!typeError}
            helperText={typeError}
            inputProps={{ maxLength: 100 }}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
              Create
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};

export default CreateDocumentType;
